<template>
  <div class="modalbox-content">
    <Loading :isActive="carregando" />

    <div class="produto-precos__hint">
      <CIcon name="cil-info" size="sm" />
      Tabelas de preço cadastradas para este produto (somente leitura).
    </div>

    <div v-if="!carregando && !precos.length" class="produto-precos__empty">
      <CIcon name="cil-ban" size="lg" />
      <p>Nenhuma tabela de preço encontrada para este produto</p>
    </div>

    <GridCardRow v-else-if="precos.length">
      <GridCard
        v-for="preco in precos"
        :key="preco.id"
        :cols="12"
        :titulo="`Tabela #${preco.tabelapreco}`"
        :subtitulo="preco.tabelapreco_desc"
        accent
      >
        <template #meta>
          <span
            class="sistema-badge"
            :class="preco.ativo ? 'sistema-badge--ativo' : 'sistema-badge--inativo'"
          >
            {{ preco.ativo ? 'Ativo' : 'Inativo' }}
          </span>
          <span class="meta-atualizacao">
            <CIcon name="cil-clock" size="sm" />
            {{ preco.dataatualizacao | datetime }}
          </span>
        </template>

        <GridCardSection label="Custo & bases">
          <div class="campos-grid">
            <GridCardField label="Valor CMV (custo)" :valor="preco.valorcmv" tipo="monetary" />
            <GridCardField label="Base flex" :valor="preco.baseflex" tipo="monetary" />
            <GridCardField label="Preço mínimo" :valor="preco.precominimo" tipo="monetary" />
            <GridCardField label="Preço final" :valor="preco.precofinal" tipo="monetary" />
            <GridCardField label="Divisor unidade" :valor="preco.divisorunidade" tipo="numero" />
          </div>
        </GridCardSection>

        <GridCardSection label="Preços por coluna · varejo">
          <div class="colunas-grid">
            <div v-for="coluna in 7" :key="`varejo-${coluna}`" class="coluna-preco">
              <div class="coluna-preco__cabecalho">Preço {{ coluna }}</div>
              <div class="coluna-preco__valor">{{ preco[`precobase${coluna}`] | monetary }}</div>
              <div class="coluna-preco__desc">
                Máx desc.: {{ formatarPercentual(preco[`percmaximodesc${coluna}`]) }}
              </div>
            </div>
          </div>
        </GridCardSection>

        <GridCardSection
          label="Preços por coluna · atacado"
          :hint="hintAtacado(preco)"
        >
          <div class="colunas-grid">
            <div v-for="coluna in 7" :key="`atacado-${coluna}`" class="coluna-preco coluna-preco--atacado">
              <div class="coluna-preco__cabecalho">Atacado {{ coluna }}</div>
              <div class="coluna-preco__valor">{{ preco[`precobase_atacado${coluna}`] | monetary }}</div>
            </div>
          </div>
        </GridCardSection>

        <GridCardSection label="Limites de negociação">
          <div class="campos-grid">
            <GridCardField label="% desc. máx. geral" :valor="preco.percmaximodesc" tipo="percent" />
            <GridCardField label="% desc. máx. negociação" :valor="preco.percmaximodesc_negociacao" tipo="percent" />
            <GridCardField label="% acréscimo máx." :valor="preco.percmaximoacresc" tipo="percent" />
          </div>
        </GridCardSection>
      </GridCard>
    </GridCardRow>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";

export default {
  name: "ProdutoPrecos",
  components: { Loading },
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      carregando: false,
      precos: [],
    };
  },
  watch: {
    "item.id": {
      immediate: true,
      handler(produtoId) {
        if (produtoId) this.carregar(produtoId);
      },
    },
  },
  methods: {
    carregar(produtoId) {
      this.carregando = true;
      get(`/admin/produtos/${encodeURIComponent(produtoId)}/precos`)
        .then((json) => {
          this.precos = json.data || [];
        })
        .catch(() => {
          this.precos = [];
        })
        .finally(() => {
          this.carregando = false;
        });
    },
    formatarPercentual(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      return `${Number(valor).toFixed(2)}%`;
    },
    hintAtacado(preco) {
      if (!preco.quantidade_minima_atacado) return null;
      const quantidadeMinima = this.formatarNumero(preco.quantidade_minima_atacado);
      const multiplo = preco.multiplo_atacado
        ? ` (múltiplo ${this.formatarNumero(preco.multiplo_atacado)})`
        : "";
      return `a partir de ${quantidadeMinima} un.${multiplo}`;
    },
    formatarNumero(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      return Number(valor).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
    },
  },
};
</script>

<style scoped>
.produto-precos__hint {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: #f9fafb;
  border-left: 3px solid #2298a3;
  border-radius: 4px;
  font-size: 13px;
  color: #475467;
  margin-bottom: 14px;
}

.produto-precos__empty {
  text-align: center;
  padding: 40px 20px;
  color: #98a2b3;
  background: #f9fafb;
  border: 1px dashed #dddce9;
  border-radius: 10px;
}

.produto-precos__empty p {
  margin: 8px 0 0;
  font-size: 13px;
}

.meta-atualizacao {
  display: flex;
  align-items: center;
  gap: 4px;
}

.campos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
}

.colunas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
}

.coluna-preco {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  background: #fff;
  border: 1px solid #dddce9;
  border-radius: 8px;
  text-align: center;
}

.coluna-preco--atacado {
  background: linear-gradient(180deg, #fff, #fafaff);
  border-color: #c5e6e9;
}

.coluna-preco__cabecalho {
  font-size: 10px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.coluna-preco__valor {
  font-size: 14px;
  font-weight: 700;
  color: #3a3e81;
  font-feature-settings: "tnum";
}

.coluna-preco--atacado .coluna-preco__valor {
  color: #1a7a83;
}

.coluna-preco__desc {
  font-size: 10px;
  color: #98a2b3;
}

.sistema-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.sistema-badge--ativo {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.sistema-badge--inativo {
  background: #f5f5f5;
  color: #999;
  border: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .campos-grid,
  .colunas-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
